// Fix 51 (2026-10-06): pantalla "Agenda iCloud". Junta en un solo lugar todo
// lo de los contactos de los iPhone de trabajo:
//  - cómo funciona (las reglas acordadas con el cliente);
//  - estado y acciones (la tarjeta de los Fix 49/50: probar conexión, vista
//    previa, automático cada hora, probar con 1 contacto, sincronizar ahora);
//  - las listas completas con buscador y descarga a Excel.
// Solo admin. La pantalla NO escribe nada por sí misma: lo único que escribe
// en iCloud son los botones de la tarjeta, que piden confirmación.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, FileSpreadsheet, Search, Info, DatabaseBackup, Download } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import ICloudContactsCard from '../components/ICloudContactsCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const TEXT = {
  es: {
    title: 'Agenda iCloud',
    subtitle: 'Clientes de la plataforma en la agenda de los iPhone de trabajo (WhatsApp Business, Telegram y Mensajes)',
    adminOnly: 'Esta pantalla es solo para administradores.',
    howTitle: 'Cómo funciona',
    how: [
      // Fix 55 (2026-10-08): también se mira el nombre.
      // Fix 56 (2026-10-09): nombre de pila + apellido, y "Agregar igual".
      'Se agregan solo los clientes cuyo teléfono no está en ningún contacto y cuyo nombre y apellido tampoco están en la agenda. Si ya están guardados con otro teléfono, no se agrega: aparece en "Mismo nombre, otro teléfono" para revisarlo; si es otra persona, se marca "Agregar igual".',
      'Los contactos cargados a mano nunca se modifican ni se borran.',
      'Los contactos que crea la plataforma llevan la nota "Agregado por Lovi" y se actualizan cuando cambian los datos del cliente (por ejemplo, cuando vuelve a comprar).',
      'Si alguien borra del teléfono un contacto que creó la plataforma, no se vuelve a crear.',
      'Con "Automático cada hora" prendido, los clientes nuevos aparecen solos en la agenda.',
    ],
    search: 'Buscar por nombre o teléfono',
    tabCreated: 'Agregados por Lovi',
    tabMissing: 'Faltan agregar',
    tabOther: 'Mismo teléfono, otro nombre',
    tabSameName: 'Mismo nombre, otro teléfono', // Fix 55
    tabHistory: 'Historial',
    helpCreated: 'Contactos que creó la plataforma en la agenda.',
    helpMissing: 'Clientes que no están en la agenda ni por teléfono ni por nombre: son los que se agregan al sincronizar.',
    // Fix 55 (2026-10-08)
    helpSameName: 'El nombre y apellido ya están en la agenda, pero con otro teléfono. No se agregan, para no repetir contactos. Si la diferencia es de 1 o 2 dígitos, casi seguro es un error de tipeo: conviene revisar cuál es el número correcto y corregirlo en el teléfono. Si es otra persona, "Agregar igual" la suma en la próxima sincronización.',
    // Fix 56 (2026-10-09): "Agregar igual"
    action: 'Acción',
    addAnyway: 'Agregar igual',
    willAdd: 'Se agregará',
    markedByHand: 'Marcado a mano',
    undo: 'Deshacer',
    confirmAddAnyway: (n) => `¿"${n}" es otra persona? Se va a agregar como contacto nuevo en la próxima sincronización. Los contactos que ya están en la agenda no se tocan.`,
    addAnywayDone: 'Marcado. Se agrega en la próxima sincronización.',
    undoDone: 'Listo, ya no se va a agregar.',
    overrideError: 'No se pudo guardar el cambio.',
    // Fix 57 (2026-10-09): "Corregir como en TikTok" (pedido del dueño)
    fixOne: 'Corregir como TikTok',
    fixAll: (n) => `Corregir ${n} como en TikTok`,
    fixHelp: 'Pone en el iPhone el nombre y el número como están en TikTok. Antes se hace un respaldo de toda la agenda; se cambian solo el nombre y ese número (fotos, mails y otros datos quedan igual) y en la nota queda cómo estaba antes. Los que "pueden ser otra persona" no se corrigen en lote: para esos está "Agregar igual".',
    confirmFixAll: (n) => `Se va a hacer un respaldo de la agenda y después se van a corregir ${n} contactos con el nombre y el número de TikTok. Se puede deshacer. ¿Seguir?`,
    confirmFixOne: (n) => `Se va a hacer un respaldo de la agenda y después se va a corregir "${n}" con el nombre y el número de TikTok. ¿Seguir?`,
    fixStarted: 'Corrección iniciada (primero se hace el respaldo).',
    fixRunning: (d, t) => (t ? `Corrigiendo… leyendo ${d} de ${t}` : 'Haciendo el respaldo y corrigiendo…'),
    fixDone: (n, f) => `Listo: ${n} contactos corregidos${f ? `, ${f} con error` : ''}. Tocá "Vista previa" para ver la lista actualizada.`,
    fixError: 'No se pudo corregir.',
    maybeOther: 'puede ser otra persona',
    sameNameCustomer: 'hay otro cliente con el mismo nombre, ya guardado con su número', // Fix 58
    platformPhone: 'Teléfono en la plataforma',
    agendaPhone: 'Teléfono en la agenda',
    digitsDiff: 'Dígitos distintos',
    typo: 'posible error de tipeo',
    helpOther: 'El teléfono ya está en la agenda con otro nombre. La plataforma no los toca: suelen ser errores de tipeo o familiares que comparten teléfono. Se pueden revisar y corregir a mano en el teléfono.',
    helpHistory: 'Últimas pruebas, vistas previas y sincronizaciones.',
    fromPreview: (d) => `Según la vista previa del ${d}. Para actualizar, tocá "Vista previa".`,
    noPreview: 'Todavía no hay vista previa: tocá "Vista previa" arriba.',
    download: 'Descargar Excel',
    downloadError: 'No se pudo descargar el Excel.',
    empty: 'No hay nada en esta lista.',
    noMatch: 'Ningún resultado para la búsqueda.',
    name: 'Nombre', phone: 'Teléfono', created: 'Agregado', updated: 'Última actualización',
    status: 'Estado', inAgenda: 'En la agenda', deleted: 'Borrado del teléfono',
    platformName: 'En la plataforma', agendaName: 'En la agenda',
    date: 'Fecha', level: 'Nivel', message: 'Detalle',
    // Fix 54 (2026-10-08): respaldo de la agenda
    backupTitle: 'Respaldo de la agenda',
    backupHelp: 'Crea un archivo .vcf con TODOS los contactos de la agenda, completos (nombre, teléfonos, mails, direcciones y notas). Crear el respaldo solo lee la agenda: no cambia nada. Se recomienda hacerlo antes de sincronizar. Si alguna vez hiciera falta, el archivo se vuelve a cargar en icloud.com, en Contactos, con la opción de importar vCard.',
    backupCreate: 'Crear respaldo',
    backupDownload: 'Descargar respaldo (.vcf)',
    backupRunning: (d, t) => (t ? `Leyendo contactos… ${d} de ${t}` : 'Leyendo contactos…'),
    backupLast: (date, n, mb) => `Último respaldo: ${date} — ${n} contactos (${mb} MB).`,
    backupGone: 'El archivo ya no está en el servidor (se reinició): hay que crear uno nuevo.',
    backupError: 'No se pudo crear el respaldo.',
    backupDownloadError: 'No se pudo descargar el respaldo.',
    backupErrors: {
      not_configured: 'Falta configurar la cuenta de iCloud.',
      auth_rejected: 'iCloud rechazó el Apple ID o la contraseña de app.',
      unreachable: 'No se pudo conectar con iCloud.',
      unexpected_response: 'iCloud respondió algo inesperado.',
      incomplete_read: 'La agenda no se leyó completa: no se guardó el respaldo. Probar de nuevo.',
      job_already_running: 'Ya hay un respaldo en curso.',
      interrupted: 'El respaldo anterior se interrumpió.',
      backup_too_large: 'El respaldo es demasiado grande para guardarlo.', // Fix 55
    },
  },
  en: {
    title: 'iCloud contacts',
    subtitle: 'Platform customers in the work iPhones address book (WhatsApp Business, Telegram and Messages)',
    adminOnly: 'This screen is for administrators only.',
    howTitle: 'How it works',
    how: [
      // Fix 55 (2026-10-08)
      // Fix 56 (2026-10-09)
      'Only customers whose phone is not in any contact and whose first and last name are not in the address book either are added. If they are already saved with another phone, the customer is not added: it shows up in "Same name, other phone" for review; if it is another person, mark "Add anyway".',
      'Contacts entered by hand are never changed or deleted.',
      'Contacts created by the platform carry the note "Agregado por Lovi" and are updated when the customer data changes (for example, when they buy again).',
      'If someone deletes from the phone a contact created by the platform, it is not created again.',
      'With "Automatic every hour" on, new customers show up in the address book on their own.',
    ],
    search: 'Search by name or phone',
    tabCreated: 'Added by Lovi',
    tabMissing: 'To be added',
    tabOther: 'Same phone, other name',
    tabSameName: 'Same name, other phone', // Fix 55
    tabHistory: 'History',
    helpCreated: 'Contacts the platform created in the address book.',
    helpMissing: 'Customers not in the address book by phone or by name: these are added when syncing.',
    // Fix 55 (2026-10-08)
    helpSameName: 'The first and last name are already in the address book, but with another phone. They are not added, to avoid repeated contacts. A 1 or 2 digit difference is almost surely a typo: check which number is right and fix it on the phone. If it is another person, "Add anyway" adds it in the next sync.',
    // Fix 56 (2026-10-09): "Add anyway"
    action: 'Action',
    addAnyway: 'Add anyway',
    willAdd: 'Will be added',
    markedByHand: 'Marked by hand',
    undo: 'Undo',
    confirmAddAnyway: (n) => `Is "${n}" another person? It will be added as a new contact in the next sync. Contacts already in the address book are not touched.`,
    addAnywayDone: 'Marked. It will be added in the next sync.',
    undoDone: 'Done, it will not be added.',
    overrideError: 'The change could not be saved.',
    // Fix 57 (2026-10-09): "Fix as in TikTok" (owner's request)
    fixOne: 'Fix as TikTok',
    fixAll: (n) => `Fix ${n} as in TikTok`,
    fixHelp: 'Sets the name and phone as they are in TikTok on the iPhone. A backup of the whole address book is made first; only the name and that phone change (photos, emails and other data stay) and the note keeps what was there before. Those that "may be another person" are not fixed in bulk: use "Add anyway" for them.',
    confirmFixAll: (n) => `A backup of the address book will be made and then ${n} contacts will be fixed with the TikTok name and phone. It can be undone. Continue?`,
    confirmFixOne: (n) => `A backup of the address book will be made and then "${n}" will be fixed with the TikTok name and phone. Continue?`,
    fixStarted: 'Fix started (the backup goes first).',
    fixRunning: (d, t) => (t ? `Fixing… reading ${d} of ${t}` : 'Making the backup and fixing…'),
    fixDone: (n, f) => `Done: ${n} contacts fixed${f ? `, ${f} failed` : ''}. Press "Preview" to see the updated list.`,
    fixError: 'Could not fix.',
    maybeOther: 'may be another person',
    sameNameCustomer: 'another customer with the same name is already saved with their number', // Fix 58
    platformPhone: 'Phone in the platform',
    agendaPhone: 'Phone in the address book',
    digitsDiff: 'Different digits',
    typo: 'possible typo',
    helpOther: 'The phone is already in the address book under another name. The platform does not touch them: usually typos or relatives sharing a phone. They can be reviewed and fixed by hand on the phone.',
    helpHistory: 'Latest tests, previews and syncs.',
    fromPreview: (d) => `From the preview of ${d}. To refresh, press "Preview".`,
    noPreview: 'There is no preview yet: press "Preview" above.',
    download: 'Download Excel',
    downloadError: 'The Excel file could not be downloaded.',
    empty: 'Nothing in this list.',
    noMatch: 'No results for this search.',
    name: 'Name', phone: 'Phone', created: 'Added', updated: 'Last update',
    status: 'Status', inAgenda: 'In the address book', deleted: 'Deleted from the phone',
    platformName: 'In the platform', agendaName: 'In the address book',
    date: 'Date', level: 'Level', message: 'Detail',
    // Fix 54 (2026-10-08): address book backup
    backupTitle: 'Address book backup',
    backupHelp: 'Creates a .vcf file with ALL the address book contacts, complete (name, phones, emails, addresses and notes). Creating the backup only reads the address book: nothing is changed. Recommended before syncing. If ever needed, the file can be loaded back on icloud.com, in Contacts, with the import vCard option.',
    backupCreate: 'Create backup',
    backupDownload: 'Download backup (.vcf)',
    backupRunning: (d, t) => (t ? `Reading contacts… ${d} of ${t}` : 'Reading contacts…'),
    backupLast: (date, n, mb) => `Last backup: ${date} — ${n} contacts (${mb} MB).`,
    backupGone: 'The file is no longer on the server (it restarted): create a new one.',
    backupError: 'The backup could not be created.',
    backupDownloadError: 'The backup could not be downloaded.',
    backupErrors: {
      not_configured: 'The iCloud account is not configured.',
      auth_rejected: 'iCloud rejected the Apple ID or the app-specific password.',
      unreachable: 'Could not reach iCloud.',
      unexpected_response: 'iCloud returned an unexpected response.',
      incomplete_read: 'The address book was not fully read: the backup was not saved. Try again.',
      job_already_running: 'A backup is already running.',
      interrupted: 'The previous backup was interrupted.',
      backup_too_large: 'The backup is too large to be stored.', // Fix 55
    },
  },
};

// Fechas en formato de EE. UU. (MM/DD/YYYY), como el resto de los reportes.
function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function matches(row, keys, q) {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  const digits = s.replace(/\D/g, '');
  if (keys.some((k) => String(row[k] ?? '').toLowerCase().includes(s))) return true;
  return digits.length >= 3 && String(row.phone || '').replace(/\D/g, '').includes(digits);
}

function ListTable({ rows, cols, q, T, testid }) {
  const keys = cols.map((c) => c.key);
  const filtered = rows.filter((r) => matches(r, keys, q));
  if (!rows.length) {
    return <p className="py-6 text-center text-[13px] text-muted-foreground">{T.empty}</p>;
  }
  if (!filtered.length) {
    return <p className="py-6 text-center text-[13px] text-muted-foreground">{T.noMatch}</p>;
  }
  return (
    <div className="max-h-[480px] overflow-auto rounded-lg border" data-testid={testid}>
      <Table>
        <TableHeader>
          <TableRow>
            {cols.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((r, i) => (
            <TableRow key={`${r.phone || ''}-${i}`}>
              {cols.map((c) => (
                <TableCell key={c.key} className={c.mono ? 'font-mono text-xs whitespace-nowrap' : 'text-xs'}>
                  {c.render ? c.render(r) : (r[c.key] || '—')}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ListHeader({ help, note, onDownload, downloading, T, testid }) {
  return (
    <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
      <div className="space-y-1">
        <p className="text-[13px] text-muted-foreground">{help}</p>
        {note && <p className="text-[12px] text-muted-foreground">{note}</p>}
      </div>
      {onDownload && (
        <Button variant="secondary" size="sm" onClick={onDownload} disabled={downloading} data-testid={testid}>
          {downloading
            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : <FileSpreadsheet className="h-4 w-4 mr-2" />}
          {T.download}
        </Button>
      )}
    </div>
  );
}

// Fix 54 (2026-10-08): respaldo de la agenda. Crear el respaldo solo LEE
// iCloud (PROPFIND/REPORT); el .vcf queda en el servidor hasta el próximo.
function BackupCard({ T }) {
  const qc = useQueryClient();
  const [downloading, setDownloading] = useState(false);
  const q = useQuery({
    queryKey: ['icloud-backup'],
    queryFn: async () => (await api.get('/admin/icloud/backup/status')).data,
    refetchInterval: (x) => (x.state.data?.status === 'running' ? 3000 : false),
  });
  const job = q.data || {};
  const running = job.status === 'running';
  const rep = job.report;
  const prog = job.progress || {};
  const errText = (code) => T.backupErrors[code] || T.backupError;

  const start = async () => {
    try {
      await api.post('/admin/icloud/backup');
      qc.invalidateQueries({ queryKey: ['icloud-backup'] });
    } catch (err) {
      toast.error(errText(err?.response?.data?.detail?.reason));
    }
  };

  const download = async () => {
    try {
      setDownloading(true);
      const res = await api.get('/admin/icloud/backup/download', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/vcard' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `lovi-icloud-respaldo_${String(job.finished_at || '').slice(0, 10) || 'respaldo'}.vcf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(T.backupDownloadError);
      qc.invalidateQueries({ queryKey: ['icloud-backup'] });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card data-testid="agenda-backup">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <DatabaseBackup className="h-4 w-4" /> {T.backupTitle}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[13px] text-muted-foreground">{T.backupHelp}</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={start} disabled={running} data-testid="agenda-backup-create">
            {running
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <DatabaseBackup className="h-4 w-4 mr-2" />}
            {T.backupCreate}
          </Button>
          <Button
            onClick={download}
            disabled={running || downloading || !job.file_available}
            data-testid="agenda-backup-download"
          >
            {downloading
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Download className="h-4 w-4 mr-2" />}
            {T.backupDownload}
          </Button>
        </div>
        {running && (
          <p className="text-[13px] text-muted-foreground" data-testid="agenda-backup-running">
            {T.backupRunning(prog.done, prog.total)}
          </p>
        )}
        {job.status === 'error' && (
          <p className="text-[13px]" style={{ color: 'var(--danger-fg)' }} data-testid="agenda-backup-error">
            {errText(job.error)}
          </p>
        )}
        {rep && !running && (
          <p className="text-[13px] text-muted-foreground" data-testid="agenda-backup-last">
            {T.backupLast(fmtDate(job.finished_at), rep.contacts, ((rep.bytes || 0) / 1048576).toFixed(1))}
            {!job.file_available && ` ${T.backupGone}`}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function AgendaPage() {
  const { lang } = useLang();
  const T = TEXT[lang === 'en' ? 'en' : 'es'];
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [downloading, setDownloading] = useState(null);

  // Mismas claves que usa la tarjeta: comparten caché y se actualizan juntas.
  const previewQ = useQuery({
    queryKey: ['icloud-preview'],
    queryFn: async () => (await api.get('/admin/icloud/preview/status')).data,
    enabled: isAdmin,
    refetchInterval: (x) => (x.state.data?.status === 'running' ? 3000 : false),
  });
  const syncQ = useQuery({
    queryKey: ['icloud-sync'],
    queryFn: async () => (await api.get('/admin/icloud/sync/status')).data,
    enabled: isAdmin,
    refetchInterval: (x) => (x.state.data?.job?.status === 'running' ? 3000 : false),
  });
  const contactsQ = useQuery({
    queryKey: ['icloud-platform-contacts'],
    queryFn: async () => (await api.get('/admin/icloud/contacts')).data,
    enabled: isAdmin,
  });
  const historyQ = useQuery({
    queryKey: ['icloud-history'],
    queryFn: async () => (await api.get('/admin/icloud/history')).data,
    enabled: isAdmin,
  });

  // Al terminar una sincronización o una vista previa, refrescar las listas.
  const syncDone = syncQ.data?.job?.finished_at;
  const previewDone = previewQ.data?.finished_at;
  useEffect(() => {
    qc.invalidateQueries({ queryKey: ['icloud-platform-contacts'] });
    qc.invalidateQueries({ queryKey: ['icloud-history'] });
  }, [syncDone, previewDone, qc]);

  const report = previewQ.data?.report;
  const missing = report?.samples?.missing || [];
  const other = report?.samples?.other_name || [];
  const sameName = report?.samples?.same_name_other_phone || []; // Fix 55

  // Fix 56 (2026-10-09): "Agregar igual" (solo guarda una marca en la
  // plataforma; el contacto se crea en la próxima sincronización).
  const overridesQ = useQuery({
    queryKey: ['icloud-name-overrides'],
    queryFn: async () => (await api.get('/admin/icloud/name-overrides')).data,
    enabled: isAdmin,
  });
  const overrideKeys = useMemo(
    () => new Set((overridesQ.data?.items || []).map((o) => o.phone_key)),
    [overridesQ.data],
  );
  const markOverride = useCallback(async (r) => {
    if (!window.confirm(T.confirmAddAnyway(r.platform_name))) return;
    try {
      await api.post('/admin/icloud/name-overrides', null,
        { params: { phone: r.phone_key || r.phone, name: r.platform_name } });
      toast.success(T.addAnywayDone);
      qc.invalidateQueries({ queryKey: ['icloud-name-overrides'] });
    } catch (err) {
      toast.error(T.overrideError);
    }
  }, [T, qc]);
  const undoOverride = useCallback(async (r) => {
    try {
      await api.post('/admin/icloud/name-overrides/remove', null,
        { params: { phone: r.phone_key || r.phone } });
      toast.success(T.undoDone);
      qc.invalidateQueries({ queryKey: ['icloud-name-overrides'] });
    } catch (err) {
      toast.error(T.overrideError);
    }
  }, [T, qc]);

  // Fix 57 (2026-10-09): "Corregir como en TikTok" (respaldo primero, en el
  // backend). En lote: los "typo" y "same_name"; nunca los "maybe_other".
  const fixQ = useQuery({
    queryKey: ['icloud-phone-fixes-status'],
    queryFn: async () => (await api.get('/admin/icloud/phone-fixes/status')).data,
    enabled: isAdmin,
    refetchInterval: (x) => (x.state.data?.status === 'running' ? 3000 : false),
  });
  const fixRunning = fixQ.data?.status === 'running';
  const runFix = useCallback(async (keys, confirmText) => {
    if (!keys.length || !window.confirm(confirmText)) return;
    try {
      await api.post('/admin/icloud/phone-fixes', null, { params: { phones: keys.join(',') } });
      toast.success(T.fixStarted);
      qc.invalidateQueries({ queryKey: ['icloud-phone-fixes-status'] });
    } catch (err) {
      toast.error(T.fixError);
    }
  }, [T, qc]);
  const created = contactsQ.data?.items || [];
  const history = historyQ.data?.items || [];
  const previewNote = report ? T.fromPreview(fmtDate(previewQ.data?.finished_at)) : T.noPreview;

  const cols = useMemo(() => ({
    created: [
      { key: 'name', label: T.name },
      { key: 'phone', label: T.phone, mono: true },
      { key: 'created_at', label: T.created, render: (r) => fmtDate(r.created_at) },
      { key: 'updated_at', label: T.updated, render: (r) => fmtDate(r.updated_at) },
      {
        key: 'status',
        label: T.status,
        render: (r) => (r.status === 'deleted_from_phone'
          ? <Badge variant="destructive">{T.deleted}</Badge>
          : <Badge variant="secondary">{T.inAgenda}</Badge>),
      },
    ],
    missing: [
      { key: 'platform_name', label: T.name },
      { key: 'phone', label: T.phone, mono: true },
      {
        // Fix 56: los que el equipo marcó "Agregar igual"
        key: 'forced',
        label: T.action,
        render: (r) => (r.forced ? (
          <span className="inline-flex items-center gap-2">
            <Badge variant="secondary">{T.markedByHand}</Badge>
            <Button size="sm" variant="ghost" onClick={() => undoOverride(r)} data-testid="agenda-override-undo">
              {T.undo}
            </Button>
          </span>
        ) : (r.same_name_customer ? (
          // Fix 58 (2026-10-09)
          <span className="text-[11px] text-muted-foreground">{T.sameNameCustomer}</span>
        ) : '')),
      },
    ],
    other_name: [
      { key: 'phone', label: T.phone, mono: true },
      { key: 'platform_name', label: T.platformName },
      { key: 'agenda_name', label: T.agendaName },
    ],
    // Fix 55 (2026-10-08)
    same_name_other_phone: [
      { key: 'platform_name', label: T.name },
      { key: 'phone', label: T.platformPhone, mono: true },
      { key: 'agenda_name', label: T.agendaName },
      { key: 'agenda_phone', label: T.agendaPhone, mono: true },
      {
        key: 'digits_diff',
        label: T.digitsDiff,
        render: (r) => (r.digits_diff && r.digits_diff <= 2
          ? <Badge variant="destructive">{r.digits_diff} · {T.typo}</Badge>
          : (r.digits_diff ?? '—')),
      },
      {
        // Fix 56 (2026-10-09): "Agregar igual" / "Deshacer"
        key: 'action',
        label: T.action,
        render: (r) => (overrideKeys.has(r.phone_key) ? (
          <span className="inline-flex items-center gap-2">
            <Badge variant="secondary">{T.willAdd}</Badge>
            <Button size="sm" variant="ghost" onClick={() => undoOverride(r)} data-testid="agenda-override-undo">
              {T.undo}
            </Button>
          </span>
        ) : (
          <span className="inline-flex flex-wrap items-center gap-2">
            {/* Fix 57 (2026-10-09) */}
            <Button
              size="sm"
              variant="secondary"
              disabled={fixRunning}
              onClick={() => runFix([r.phone_key], T.confirmFixOne(r.platform_name))}
              data-testid="agenda-fix-one"
            >
              {T.fixOne}
            </Button>
            <Button size="sm" variant="outline" onClick={() => markOverride(r)} data-testid="agenda-override-add">
              {T.addAnyway}
            </Button>
            {r.kind === 'maybe_other' && (
              <span className="text-[11px] text-muted-foreground">{T.maybeOther}</span>
            )}
          </span>
        )),
      },
    ],
    history: [
      { key: 'created_at', label: T.date, render: (r) => fmtDate(r.created_at) },
      {
        key: 'level',
        label: T.level,
        render: (r) => (
          <Badge variant={r.level === 'info' ? 'secondary' : 'destructive'}>{r.level}</Badge>
        ),
      },
      { key: 'message', label: T.message },
    ],
  }), [T, overrideKeys, markOverride, undoOverride, runFix, fixRunning]); // Fix 56 + 57

  const download = async (kind) => {
    try {
      setDownloading(kind);
      const res = await api.get('/admin/icloud/export', {
        params: { kind },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `lovi-agenda-${kind}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(T.downloadError);
    } finally {
      setDownloading(null);
    }
  };

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title={T.title} />
        <EmptyState message={T.adminOnly} testid="agenda-admin-only" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <PageHeader title={T.title} subtitle={T.subtitle} />

      <Card data-testid="agenda-how-it-works">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Info className="h-4 w-4" /> {T.howTitle}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc space-y-1 pl-5 text-[13px] text-muted-foreground">
            {T.how.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </CardContent>
      </Card>

      {/* Fix 54 (2026-10-08): respaldo antes de sincronizar */}
      <BackupCard T={T} />

      <ICloudContactsCard showOtherNameTable={false} />

      <Card data-testid="agenda-lists">
        <CardContent className="space-y-4 pt-6">
          <div className="relative max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={T.search}
              className="pl-8"
              data-testid="agenda-search"
            />
          </div>
          <Tabs defaultValue="created">
            <TabsList className="flex h-auto flex-wrap">
              <TabsTrigger value="created" data-testid="agenda-tab-created">
                {T.tabCreated} ({created.length})
              </TabsTrigger>
              <TabsTrigger value="missing" data-testid="agenda-tab-missing">
                {T.tabMissing} ({missing.length})
              </TabsTrigger>
              <TabsTrigger value="other_name" data-testid="agenda-tab-other">
                {T.tabOther} ({other.length})
              </TabsTrigger>
              <TabsTrigger value="same_name_other_phone" data-testid="agenda-tab-same-name">
                {T.tabSameName} ({sameName.length})
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="agenda-tab-history">
                {T.tabHistory}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="created">
              <ListHeader
                help={T.helpCreated}
                onDownload={() => download('created')}
                downloading={downloading === 'created'}
                T={T}
                testid="agenda-download-created"
              />
              <ListTable rows={created} cols={cols.created} q={q} T={T} testid="agenda-list-created" />
            </TabsContent>

            <TabsContent value="missing">
              <ListHeader
                help={T.helpMissing}
                note={previewNote}
                onDownload={() => download('missing')}
                downloading={downloading === 'missing'}
                T={T}
                testid="agenda-download-missing"
              />
              <ListTable rows={missing} cols={cols.missing} q={q} T={T} testid="agenda-list-missing" />
            </TabsContent>

            <TabsContent value="other_name">
              <ListHeader
                help={T.helpOther}
                note={previewNote}
                onDownload={() => download('other_name')}
                downloading={downloading === 'other_name'}
                T={T}
                testid="agenda-download-other"
              />
              <ListTable rows={other} cols={cols.other_name} q={q} T={T} testid="agenda-list-other" />
            </TabsContent>

            {/* Fix 55 (2026-10-08): mismo nombre en la agenda con otro teléfono */}
            <TabsContent value="same_name_other_phone">
              <ListHeader
                help={T.helpSameName}
                note={previewNote}
                onDownload={() => download('same_name_other_phone')}
                downloading={downloading === 'same_name_other_phone'}
                T={T}
                testid="agenda-download-same-name"
              />
              {/* Fix 57 (2026-10-09): corregir en lote como en TikTok */}
              {(() => {
                const bulk = sameName.filter((r) => r.phone_key && r.kind !== 'maybe_other'
                  && !overrideKeys.has(r.phone_key));
                const rep = fixQ.data?.report;
                return (
                  <div className="mb-3 space-y-2 rounded-lg border p-3" data-testid="agenda-fix-bar">
                    <p className="text-[12px] text-muted-foreground">{T.fixHelp}</p>
                    <Button
                      size="sm"
                      disabled={fixRunning || bulk.length === 0}
                      onClick={() => runFix(bulk.map((r) => r.phone_key), T.confirmFixAll(bulk.length))}
                      data-testid="agenda-fix-all"
                    >
                      {fixRunning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {T.fixAll(bulk.length)}
                    </Button>
                    {fixRunning && (
                      <p className="text-[12px] text-muted-foreground" data-testid="agenda-fix-running">
                        {T.fixRunning(fixQ.data?.progress?.done, fixQ.data?.progress?.total)}
                      </p>
                    )}
                    {!fixRunning && rep && (
                      <p className="text-[12px] text-muted-foreground" data-testid="agenda-fix-result">
                        {T.fixDone(rep.fixed, rep.failed)}
                      </p>
                    )}
                    {!fixRunning && fixQ.data?.status === 'error' && (
                      <p className="text-[12px]" style={{ color: 'var(--danger-fg)' }}>{T.fixError}</p>
                    )}
                  </div>
                );
              })()}
              <ListTable rows={sameName} cols={cols.same_name_other_phone} q={q} T={T} testid="agenda-list-same-name" />
            </TabsContent>

            <TabsContent value="history">
              <ListHeader help={T.helpHistory} T={T} />
              <ListTable rows={history} cols={cols.history} q={q} T={T} testid="agenda-list-history" />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
